import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row } from '@/components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import effortService from '../../../services/effortService';
import type { FormLabourOvertimeAllocationRow } from '../../../utils/labourOvertimeDisplay';
import { isLabourOtBucketSumValid, LABOUR_OT_BUCKET_SUM_EPS } from '../../../utils/labourOvertimeDisplay';

const ADJUST_OT_PERM = 'form:adjust_labour_ot_buckets';

export type LabourOtFormBucketEditorProps = {
  formId: string;
  formStatus: string;
  labourOvertimeAllocations: FormLabourOvertimeAllocationRow[];
  onAfterSave?: () => void | Promise<void>;
};

function aggregateFormBuckets(allocations: FormLabourOvertimeAllocationRow[]): {
  straight: number;
  o15: number;
  o20: number;
  totalHours: number;
} {
  let straight = 0;
  let o15 = 0;
  let o20 = 0;
  let totalHours = 0;
  for (const a of allocations) {
    if (a.id == null || (a.inputHours ?? 0) <= 1e-4) continue;
    straight += Number(a.labourStraightHours ?? 0);
    o15 += Number(a.labourOvertime15Hours ?? 0);
    o20 += Number(a.labourOvertime20Hours ?? 0);
    totalHours += Number(a.inputHours ?? 0);
  }
  return { straight, o15, o20, totalHours };
}

export const LabourOtFormBucketEditor: React.FC<LabourOtFormBucketEditorProps> = ({
  formId,
  formStatus,
  labourOvertimeAllocations,
  onAfterSave
}) => {
  const { hasPermission } = usePermissions();
  const canEdit = ['pending', 'revisions completed'].includes(formStatus?.toLowerCase()) && hasPermission(ADJUST_OT_PERM);

  const activeRows = useMemo(
    () =>
      labourOvertimeAllocations.filter((a) => a.id != null && (a.inputHours ?? 0) > 1e-4),
    [labourOvertimeAllocations]
  );

  const agg = useMemo(() => aggregateFormBuckets(labourOvertimeAllocations), [labourOvertimeAllocations]);

  const [straight, setStraight] = useState(agg.straight);
  const [o15, setO15] = useState(agg.o15);
  const [o20, setO20] = useState(agg.o20);
  const [manualReason, setManualReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStraight(agg.straight);
    setO15(agg.o15);
    setO20(agg.o20);
    setError(null);
  }, [agg.straight, agg.o15, agg.o20, labourOvertimeAllocations]);

  const total = agg.totalHours;
  const sumValid = isLabourOtBucketSumValid(straight, o15, o20, total);
  const remainder = total - (straight + o15 + o20);

  const handleSave = async () => {
    setError(null);
    if (!sumValid) {
      setError('Straight + 1.5× + 2× must equal total labour hours on this form.');
      return;
    }
    setSaving(true);
    try {
      const rowVersions = activeRows
        .filter((a) => a.updatedAt)
        .map((a) => ({ id: a.id as number, expectedUpdatedAt: a.updatedAt as string }));
      await effortService.applyFormLabourOvertimeBuckets({
        formId,
        labourStraightHours: straight,
        labourOvertime15Hours: o15,
        labourOvertime20Hours: o20,
        manualReason: manualReason.trim() || undefined,
        rowVersions: rowVersions.length > 0 ? rowVersions : undefined
      });
      if (onAfterSave) {
        await onAfterSave();
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string; code?: string } } })?.response?.data?.error ||
        (e instanceof Error ? e.message : 'Save failed');
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <p className="tw-text-sm tw-text-content-muted tw-mb-0">
        You do not have permission to adjust labour OT buckets on this submission.
      </p>
    );
  }

  if (activeRows.length === 0) {
    return (
      <p className="tw-text-sm tw-text-content-muted tw-mb-0">
        No labour OT allocation rows with hours are available for this submission. Close and try again
        after the list refreshes, or contact support if hours are shown on the card but buckets stay empty.
      </p>
    );
  }

  return (
    <div className="tw-py-2">
      <p className="tw-text-sm tw-text-content-muted tw-mb-3">
        Totals apply to this entire submission. Straight, 1.5×, and 2× must add up to{' '}
        <strong>{total.toFixed(2)} h</strong>; hours are redistributed across tasks bottom-up (last task first).
      </p>
      <Row className="tw-items-end">
        <Col xs={6} md={3}>
          <Form.Label className="tw-text-sm tw-mb-0">Straight (h)</Form.Label>
          <Form.Control
            type="number"
            step="0.05"
            size="sm"
            value={Number.isFinite(straight) ? straight : 0}
            onChange={(e) => setStraight(parseFloat(e.target.value) || 0)}
          />
        </Col>
        <Col xs={6} md={3}>
          <Form.Label className="tw-text-sm tw-mb-0">OT 1.5× (h)</Form.Label>
          <Form.Control
            type="number"
            step="0.05"
            size="sm"
            value={Number.isFinite(o15) ? o15 : 0}
            onChange={(e) => setO15(parseFloat(e.target.value) || 0)}
          />
        </Col>
        <Col xs={6} md={3}>
          <Form.Label className="tw-text-sm tw-mb-0">OT 2× (h)</Form.Label>
          <Form.Control
            type="number"
            step="0.05"
            size="sm"
            value={Number.isFinite(o20) ? o20 : 0}
            onChange={(e) => setO20(parseFloat(e.target.value) || 0)}
          />
        </Col>
        <Col xs={12} md={3}>
          <Form.Label className="tw-text-sm tw-mb-0">Total (fixed)</Form.Label>
          <div
            className={`tw-block tw-w-full tw-rounded tw-border tw-px-3 tw-py-1 tw-text-sm tw-bg-surface ${sumValid ? 'tw-border-line-input' : 'tw-border-danger tw-text-danger'}`}
            style={{ minHeight: '31px' }}
          >
            {total.toFixed(2)} h
            {!sumValid && (
              <span className="tw-ml-1 tw-text-sm">
                (off by {Math.abs(remainder).toFixed(2)}; tol ±{LABOUR_OT_BUCKET_SUM_EPS})
              </span>
            )}
          </div>
        </Col>
      </Row>
      <div className="tw-mt-2">
        <Form.Label className="tw-text-sm tw-mb-0">Reason (optional)</Form.Label>
        <Form.Control
          type="text"
          size="sm"
          value={manualReason}
          onChange={(e) => setManualReason(e.target.value)}
          placeholder="Note for audit history"
        />
      </div>
      <div className="tw-mt-3">
        <Button
          type="button"
          variant="primary"
          size="sm"
          disabled={saving || !sumValid}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save form buckets'}
        </Button>
      </div>
      {error && <div className="tw-text-danger tw-text-sm tw-mt-2">{error}</div>}
    </div>
  );
};

export default LabourOtFormBucketEditor;
